import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useNavigate } from 'react-router-dom';
import { autoLotteryService, AutoLotteryRound } from '../services/autoLotteryService';

const LotteryWinners: React.FC = () => {
  const navigate = useNavigate();
  const [roundHistory, setRoundHistory] = useState<AutoLotteryRound[]>([]);
  const [currentRound, setCurrentRound] = useState<AutoLotteryRound | null>(null);
  const [stats, setStats] = useState(autoLotteryService.getStats());

  useEffect(() => {
    // Load lottery data
    setRoundHistory(autoLotteryService.getRoundHistory());
    setCurrentRound(autoLotteryService.getCurrentRound());
    setStats(autoLotteryService.getStats());

    // Listen for updates
    const handleRoundUpdate = (round: AutoLotteryRound) => {
      setRoundHistory(autoLotteryService.getRoundHistory());
      setCurrentRound(autoLotteryService.getCurrentRound());
      setStats(autoLotteryService.getStats());
    };

    autoLotteryService.onRoundUpdate(handleRoundUpdate);
  }, []);

  return (
    <div className="h-full bg-black text-green-400 flex flex-col">
      <div className="p-6 border-b border-green-500/30 bg-[#030306]">
        <div className="text-green-300 font-bold text-xl">Lottery Winners</div>
        <div className="text-green-500/70 text-sm">Celebrating innovative apps built with Pyth Entropy</div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* Stats Overview */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 bg-green-500/10 border-green-500/30">
            <div className="text-green-400/70 text-sm">Total Rounds</div>
            <div className="text-2xl font-bold text-green-400">{stats.totalRounds}</div>
          </Card>
          <Card className="p-4 bg-blue-500/10 border-blue-500/30">
            <div className="text-blue-400/70 text-sm">Total Prizes</div>
            <div className="text-2xl font-bold text-blue-400">{stats.totalPrizeDistributed.toFixed(2)} ETH</div>
          </Card>
          <Card className="p-4 bg-purple-500/10 border-purple-500/30">
            <div className="text-purple-400/70 text-sm">Active Players</div>
            <div className="text-2xl font-bold text-purple-400">{stats.activePlayers}</div>
          </Card>
        </div>
        {/* Recent Winners */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-green-400 mb-4">🏆 Recent Winners</h2>
          <div className="space-y-3">
            {roundHistory.filter(round => round.winner).slice(0, 10).map((round, idx) => (
              <Card key={round.id} className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">{round.winner?.avatar}</div>
                    <div>
                      <div className="text-green-400 font-medium">{round.winner?.name}</div>
                      <div className="text-green-400/70 text-sm">{round.winner?.address}</div>
                      <div className="text-green-400/60 text-xs">
                        Round #{round.id} • {new Date(round.endTime).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-bold text-lg">{round.prizePool.toFixed(3)} ETH</div>
                    <div className="text-green-400/70 text-sm">Prize Won</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Current Round Status */}
        {currentRound && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-green-400 mb-4">⚡ Current Round</h2>
            <Card className="p-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/30">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-blue-400/70 text-sm">Round ID</div>
                  <div className="text-2xl font-bold text-blue-400">#{currentRound.id}</div>
                </div>
                <div>
                  <div className="text-blue-400/70 text-sm">Participants</div>
                  <div className="text-2xl font-bold text-blue-400">{currentRound.participants.length}</div>
                </div>
                <div>
                  <div className="text-blue-400/70 text-sm">Prize Pool</div>
                  <div className="text-2xl font-bold text-blue-400">{currentRound.prizePool.toFixed(3)} ETH</div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <div className="text-blue-400/70 text-sm">Status</div>
                <div className="text-lg font-medium text-blue-400">
                  {currentRound.status === 'active' ? '🟢 Active - Winner will be announced soon!' : '🔴 Ended'}
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <Button onClick={() => navigate('/lottery/play')} className="bg-green-600/30 border border-green-500/40 text-green-300 hover:bg-green-500/20">
            Play Again
          </Button>
          <Button onClick={() => navigate('/lottery')} variant="outline" className="border-green-500/40 text-green-400/80 hover:bg-green-500/10">
            Back to Lottery Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LotteryWinners;
